
import React, { useState, useEffect } from "react";
import { usersService } from "@/services";
import { estatesService } from "@/services";
import { useAuth } from "@/lib/AuthContext";
import { useEstateContext } from "@/lib/EstateContext";
import { supabase } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Settings as SettingsIcon,
  Bell,
  Moon,
  Sun,
  Mail,
  Smartphone,
  Save,
  Shield,
  User,
  Trash2,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

export default function Settings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const { myEstates, activeEstate, activeEstateId, refreshEstates, setActiveEstate } = useEstateContext();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteEstateConfirmation, setDeleteEstateConfirmation] = useState('');
  const [showDeleteEstateDialog, setShowDeleteEstateDialog] = useState(false);

  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    status_updates: true,
  });

  useEffect(() => {
    // Load notification preferences from user, with defaults if not set
    if (user) {
      const preferences = user.notification_preferences || {
        email: true,
        status_updates: true,
      };
      setNotificationSettings(preferences);
    }
  }, [user]);

  useEffect(() => {
    // Apply theme
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (data) => {
      return await usersService.updateMe(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      toast.success("⚙️ Notification settings saved successfully!", { duration: 3000 });
    },
    onError: () => {
      toast.error("Failed to save settings. Please try again.");
    },
  });

  const deleteEstateMutation = useMutation({
    mutationFn: async (estateId) => {
      // Ownership must be verified BEFORE any irreversible Paystack action.
      // Only the estate's owner_user_id may delete it (matches the DELETE RLS
      // policy) — stop immediately for non-owners so we never cancel a live
      // subscription for an estate we can't actually delete.
      if (activeEstate?.owner_user_id !== user?.id) {
        throw new Error('Only the estate owner can delete this estate.');
      }

      // If the estate has an active or past_due Paystack subscription, cancel it
      // first to prevent the manager being billed for a deleted estate.
      // (past_due still has a live, retrying Paystack subscription — see
      // ManageSubscription.jsx's isActiveSubscription for the same definition.)
      const hasActiveSub = ['active', 'past_due'].includes(activeEstate?.subscription_status)
        && activeEstate?.paystack_subscription_code;

      if (hasActiveSub) {
        const { data: { session: cancelSession } } = await supabase.auth.getSession();
        const response = await fetch('/api/paystack-manage', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cancelSession?.access_token}`,
          },
          body: JSON.stringify({
            action: 'cancel',
            subscriptionCode: activeEstate.paystack_subscription_code,
            estateId,
          }),
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Failed to cancel subscription before deletion. Please cancel the subscription manually first, then delete the estate.');
        }
      }

      await estatesService.deleteEstate(estateId);
    },
    onSuccess: async () => {
      toast.success(`"${activeEstate?.name}" deleted successfully.`, { duration: 3000 });
      await refreshEstates();
      // Switch to another estate if the user still has some, otherwise go to CreateEstate
      const remaining = myEstates.filter(e => e.id !== activeEstateId);
      if (remaining.length > 0) {
        setActiveEstate(remaining[0].id);
        navigate('/EstateDashboard');
      } else {
        navigate('/CreateEstate');
      }
    },
    onError: (error) => {
      console.error('Delete estate error:', error);
      toast.error(error.message || 'Failed to delete estate. Please try again or contact support.');
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      return await usersService.deleteAccount();
    },
    onSuccess: (data) => {
      toast.success("🗑️ Account deleted successfully. Your data has been anonymized.", { duration: 3000 });
      // Redirect to login after a brief delay
      setTimeout(() => {
        window.location.href = '/Login';
      }, 1500);
    },
    onError: (error) => {
      const message = error.message || "Failed to delete account. Please try again.";
      toast.error(message, { duration: 5000 });
    },
  });

  const handleNotificationChange = (key) => {
    const updated = { ...notificationSettings, [key]: !notificationSettings[key] };
    setNotificationSettings(updated);
  };

  const handleSave = () => {
    updateSettingsMutation.mutate({
      notification_preferences: notificationSettings,
    });
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    toast.success(newTheme === 'dark' ? "🌙 Dark mode enabled" : "☀️ Light mode enabled", { 
      duration: 2000 
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F0] dark:bg-gray-900">
        <Card className="p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-4">Please Login</h2>
          <Button onClick={() => navigate('/Login')}>
            Login to Access Settings
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 transition-colors bg-[#F0F0F0] dark:bg-gray-900">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <SettingsIcon className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            Manage your preferences and account settings
          </p>
        </div>

        <div className="space-y-6">
          {/* Appearance */}
          <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              {theme === 'light' ? (
                <Sun className="w-6 h-6 text-amber-500" />
              ) : (
                <Moon className="w-6 h-6 text-blue-400" />
              )}
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Appearance</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Customize how FixMyHood looks
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center gap-3">
                {theme === 'light' ? (
                  <Sun className="w-5 h-5 text-amber-500" />
                ) : (
                  <Moon className="w-5 h-5 text-blue-400" />
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {theme === 'light' ? 'Light Mode' : 'Dark Mode'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Switch between light and dark themes
                  </p>
                </div>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
              />
            </div>
          </Card>

          {/* Notifications */}
          <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Notifications</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Choose what updates you want to receive
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Email Notifications</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Receive updates via email
                    </p>
                  </div>
                </div>
                <Switch
                  checked={notificationSettings.email}
                  onCheckedChange={() => handleNotificationChange('email')}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Status Updates</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Get notified when your reports are updated
                    </p>
                  </div>
                </div>
                <Switch
                  checked={notificationSettings.status_updates}
                  onCheckedChange={() => handleNotificationChange('status_updates')}
                />
              </div>
            </div>

            <Separator className="my-6" />

            <Button
              onClick={handleSave}
              disabled={updateSettingsMutation.isPending}
              className="w-full bg-[#1883FF] hover:bg-[#0066CC] rounded-[24px] md:rounded-[40px]"
            >
              <Save className="w-4 h-4 mr-2" />
              {updateSettingsMutation.isPending ? "Saving..." : "Save Notification Settings"}
            </Button>
          </Card>

          {/* Privacy & Security */}
          <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Privacy & Security</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Manage your data and security
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white mb-1">
                      Your data is secure
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      We use industry-standard encryption to protect your information. 
                      Your email and personal details are never shared with third parties.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                <div className="flex items-start gap-3">
                  <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white mb-1">
                      Location Privacy
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Location data is only used to geotag your reports. 
                      It's never tracked in the background or shared publicly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Legal Documents */}
          <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Legal</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Review our policies and terms
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => navigate('/PrivacyPolicy')}
              >
                <Shield className="w-4 h-4 mr-2" />
                Privacy Policy
              </Button>

              <Button
                variant="outline"
                className="w-full justify-start hover:bg-purple-50 dark:hover:bg-purple-900/20"
                onClick={() => navigate('/TermsOfService')}
              >
                <Shield className="w-4 h-4 mr-2" />
                Terms of Service
              </Button>

              <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  ✓ POPIA Compliant • Beta Testing Phase • Last Updated: 16 February 2026
                </p>
              </div>
            </div>
          </Card>

          {/* Delete Estate Section — estate managers only, and only the estate owner */}
          {user?.user_role === 'estate_manager' && activeEstate && activeEstate.owner_user_id === user?.id && (
            <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Delete Estate</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Remove an estate from your account
                  </p>
                </div>
              </div>

              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white mb-1">
                      Currently managing: <span className="text-amber-700 dark:text-amber-400">{activeEstate.name}</span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                      This will permanently delete this estate and all its data (issues, units, contractors).
                      {myEstates.length > 1
                        ? ` You will be redirected to one of your other ${myEstates.length - 1} estate(s).`
                        : ' This is your only estate — you will be sent to create a new one.'}
                    </p>
                  </div>
                </div>

                <AlertDialog open={showDeleteEstateDialog} onOpenChange={setShowDeleteEstateDialog}>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-600 dark:hover:bg-amber-900/30"
                      disabled={deleteEstateMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete "{activeEstate.name}"
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="w-5 h-5" />
                        Delete Estate Permanently
                      </AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div className="space-y-3 pt-2">
                          <p className="text-sm">
                            This will permanently delete <strong>{activeEstate.name}</strong> and all its associated data. This <strong>cannot be undone</strong>.
                          </p>
                          <div className="pt-3 border-t">
                            <Label htmlFor="delete-estate-confirmation" className="text-sm font-medium">
                              Type the estate name <strong>{activeEstate.name}</strong> to confirm:
                            </Label>
                            <Input
                              id="delete-estate-confirmation"
                              type="text"
                              placeholder={activeEstate.name}
                              value={deleteEstateConfirmation}
                              onChange={(e) => setDeleteEstateConfirmation(e.target.value)}
                              className="mt-2"
                            />
                          </div>
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel
                        onClick={() => setDeleteEstateConfirmation('')}
                        disabled={deleteEstateMutation.isPending}
                      >
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={(e) => {
                          e.preventDefault();
                          if (deleteEstateConfirmation === activeEstate.name) {
                            deleteEstateMutation.mutate(activeEstateId);
                            setShowDeleteEstateDialog(false);
                            setDeleteEstateConfirmation('');
                          } else {
                            toast.error(`Please type the estate name exactly to confirm`);
                          }
                        }}
                        disabled={deleteEstateConfirmation !== activeEstate.name || deleteEstateMutation.isPending}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        {deleteEstateMutation.isPending ? 'Deleting...' : 'Delete Estate'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          )}

          {/* Account Actions */}
          <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Account Actions</h2>
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={async () => {
                  if (confirm("Are you sure you want to logout?")) {
                    await logout();
                  }
                }}
              >
                Logout
              </Button>

              <Separator className="my-4" />

              {/* Delete Account Section */}
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white mb-1">
                      Danger Zone
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                      Permanently delete your account and anonymize your personal data in compliance with POPIA.
                    </p>
                  </div>
                </div>

                <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      className="w-full"
                      disabled={deleteAccountMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete My Account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-5 h-5" />
                        Delete Account Permanently
                      </AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div className="space-y-3 pt-2">
                          <p className="text-sm">
                            This action <strong>cannot be undone</strong>. When you delete your account:
                          </p>
                          <ul className="text-sm space-y-2 list-disc pl-5">
                            <li>Your personal data (email, name, phone) will be anonymized</li>
                            <li>You will immediately lose access to your account</li>
                            {user?.user_role === 'estate_manager' && (
                              <li className="text-amber-700 dark:text-amber-400 font-medium">
                                Your estate will become unmanaged and require reassignment
                              </li>
                            )}
                            <li>Your reports and activity history will be preserved for operational continuity</li>
                            <li>This complies with POPIA data protection requirements</li>
                          </ul>
                          <div className="pt-3 border-t">
                            <Label htmlFor="delete-confirmation" className="text-sm font-medium">
                              Type <strong>DELETE</strong> to confirm:
                            </Label>
                            <Input
                              id="delete-confirmation"
                              type="text"
                              placeholder="DELETE"
                              value={deleteConfirmation}
                              onChange={(e) => setDeleteConfirmation(e.target.value)}
                              className="mt-2"
                            />
                          </div>
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel 
                        onClick={() => {
                          setDeleteConfirmation('');
                        }}
                        disabled={deleteAccountMutation.isPending}
                      >
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={(e) => {
                          e.preventDefault();
                          if (deleteConfirmation === 'DELETE') {
                            deleteAccountMutation.mutate();
                            setShowDeleteDialog(false);
                            setDeleteConfirmation('');
                          } else {
                            toast.error('Please type DELETE to confirm');
                          }
                        }}
                        disabled={deleteConfirmation !== 'DELETE' || deleteAccountMutation.isPending}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {deleteAccountMutation.isPending ? (
                          <>Deleting...</>
                        ) : (
                          <>Delete Account</>
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
